import React, { useState, useEffect, useRef, useCallback } from 'react';
import styled from 'styled-components';
import { Tag, TagData } from './types';
import { msToTimecode } from './utils';
import { canPlayVideo, transcodeVideo } from './videoTranscoder';

const AppContainer = styled.div`
  display: flex;
  flex-direction: column;
  height: 100vh;
  width: 100vw;
  background: linear-gradient(135deg, #01162E 0%, #001D37 20%, #002746 40%, #013155 60%, #003A63 80%, #01426D 100%);
  background-attachment: fixed;
  color: #ffffff;
  overflow: hidden;
  font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', 'Roboto', 'Oxygen',
    'Ubuntu', 'Cantarell', 'Fira Sans', 'Droid Sans', 'Helvetica Neue', sans-serif;
`;

const ControlsTop = styled.div`
  display: flex;
  gap: 10px;
  padding: 10px;
  background: rgba(1, 22, 46, 0.8);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(1, 58, 99, 0.5);
  flex-wrap: wrap;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const FileInput = styled.input`
  display: none;
`;

const FileInputLabel = styled.label`
  padding: 8px 16px;
  background: linear-gradient(135deg, #002746 0%, #013155 100%);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  display: inline-block;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: linear-gradient(135deg, #013155 0%, #003A63 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(1, 66, 109, 0.5);
    border-color: rgba(1, 66, 109, 0.8);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`;

const Button = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #003A63 0%, #01426D 100%);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 8px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  box-shadow: 0 2px 8px rgba(0, 0, 0, 0.3);

  &:hover {
    background: linear-gradient(135deg, #013155 0%, #003A63 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(1, 66, 109, 0.5);
    border-color: rgba(1, 66, 109, 0.8);
  }

  &:active {
    transform: translateY(0);
    box-shadow: 0 2px 6px rgba(0, 0, 0, 0.3);
  }
`;

const SyncControls = styled.div`
  padding: 10px;
  background: rgba(1, 22, 46, 0.7);
  backdrop-filter: blur(10px);
  border-bottom: 1px solid rgba(1, 58, 99, 0.5);
  display: flex;
  align-items: center;
  gap: 10px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.3);
`;

const OffsetInput = styled.input`
  padding: 6px 10px;
  background: rgba(0, 39, 70, 0.8);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 6px;
  width: 150px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #01426D;
    box-shadow: 0 0 0 3px rgba(1, 66, 109, 0.3);
    background: rgba(0, 39, 70, 1);
  }
`;

const Content = styled.div`
  display: flex;
  flex: 1;
  overflow: hidden;
`;

const VideosContainer = styled.div`
  display: flex;
  flex: 3;
  gap: 10px;
  padding: 10px;
  overflow: hidden;
`;

const VideoWrapper = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  position: relative;
  background-color: #000;
  border-radius: 12px;
  overflow: hidden;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.5);
  border: 1px solid rgba(1, 66, 109, 0.3);
`;

const SyncButton = styled(Button)<{ $active?: boolean }>`
  position: absolute;
  top: 10px;
  left: 10px;
  z-index: 10;
  padding: 6px 12px;
  font-size: 12px;
  background: ${props => props.$active 
    ? 'linear-gradient(135deg, #013155 0%, #003A63 100%)' 
    : 'rgba(0, 39, 70, 0.8)'};
  border: 1px solid rgba(1, 66, 109, 0.6);
  color: #ffffff;
  backdrop-filter: blur(10px);
`;

const VideoPlayer = styled.video`
  width: 100%;
  flex: 1;
  object-fit: contain;
  background-color: #000;
`;

const TimelineVisualization = styled.div`
  height: 60px;
  width: 100%;
  background: rgba(0, 0, 0, 0.5);
  position: relative;
  border-top: 1px solid rgba(1, 66, 109, 0.3);
  border-bottom: 1px solid rgba(1, 66, 109, 0.3);
  overflow: hidden;
  margin: 0;
  padding: 0;
`;

const SequenceBar = styled.div<{ $left: number; $width: number }>`
  position: absolute;
  left: ${props => props.$left}%;
  width: ${props => props.$width}%;
  height: 100%;
  background: linear-gradient(
    to right,
    rgba(1, 66, 109, 0.8) 0%,
    rgba(1, 66, 109, 0.4) 50%,
    rgba(1, 66, 109, 0.8) 100%
  );
  cursor: pointer;
  transition: all 0.2s;
  border-left: 1px solid rgba(95, 168, 211, 0.6);
  border-right: 1px solid rgba(95, 168, 211, 0.6);

  &:hover {
    background: linear-gradient(
      to right,
      rgba(1, 66, 109, 1) 0%,
      rgba(1, 66, 109, 0.6) 50%,
      rgba(1, 66, 109, 1) 100%
    );
    border-color: rgba(95, 168, 211, 1);
  }
`;

const TagMarker = styled.div<{ $left: number; $color?: string }>`
  position: absolute;
  left: ${props => props.$left}%;
  width: 2px;
  height: 100%;
  background: ${props => props.$color || '#5FA8D3'};
  cursor: pointer;
  transition: all 0.2s;
  z-index: 2;

  &:hover {
    width: 3px;
    background: ${props => props.$color || '#7BB8D8'};
    box-shadow: 0 0 4px ${props => props.$color || '#5FA8D3'};
  }

  &::before {
    content: '';
    position: absolute;
    bottom: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-bottom: 6px solid ${props => props.$color || '#5FA8D3'};
  }
`;

const CurrentPositionIndicator = styled.div<{ $left: number }>`
  position: absolute;
  left: ${props => props.$left}%;
  width: 2px;
  height: 100%;
  background: #ffffff;
  z-index: 10;
  box-shadow: 0 0 4px rgba(255, 255, 255, 0.8);
  pointer-events: none;

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 50%;
    transform: translateX(-50%);
    width: 0;
    height: 0;
    border-left: 4px solid transparent;
    border-right: 4px solid transparent;
    border-top: 6px solid #ffffff;
  }
`;

const TagsPanel = styled.div`
  flex: 1;
  display: flex;
  flex-direction: column;
  background: rgba(1, 22, 46, 0.8);
  backdrop-filter: blur(10px);
  border-left: 1px solid rgba(1, 58, 99, 0.5);
  padding: 10px;
  min-width: 300px;
  max-width: 400px;
  box-shadow: -2px 0 10px rgba(0, 0, 0, 0.3);
`;

const TagsPanelTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 14px;
  color: rgba(255, 255, 255, 0.8);
  font-weight: 500;
`;

const TagsList = styled.div`
  flex: 1;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 4px;

  &::-webkit-scrollbar {
    width: 8px;
  }

  &::-webkit-scrollbar-track {
    background: rgba(0, 39, 70, 0.3);
    border-radius: 4px;
  }

  &::-webkit-scrollbar-thumb {
    background: rgba(1, 66, 109, 0.6);
    border-radius: 4px;

    &:hover {
      background: rgba(1, 66, 109, 0.8);
    }
  }
`;

const TagItem = styled.div<{ $selected?: boolean }>`
  padding: 8px;
  background: ${props => props.$selected 
    ? 'linear-gradient(135deg, #003A63 0%, #01426D 100%)' 
    : 'rgba(0, 39, 70, 0.5)'};
  border-radius: 8px;
  cursor: pointer;
  display: flex;
  gap: 10px;
  transition: all 0.3s ease;
  border: 1px solid ${props => props.$selected ? 'rgba(1, 66, 109, 0.8)' : 'rgba(1, 66, 109, 0.3)'};

  &:hover {
    background: ${props => props.$selected 
      ? 'linear-gradient(135deg, #013155 0%, #003A63 100%)' 
      : 'rgba(0, 39, 70, 0.7)'};
    transform: translateX(2px);
    border-color: rgba(1, 66, 109, 0.6);
  }
`;

const TagTimecode = styled.span<{ $selected?: boolean }>`
  font-weight: bold;
  color: ${props => props.$selected ? '#ffffff' : '#5FA8D3'};
  min-width: 60px;
`;

const TagLabel = styled.span<{ $selected?: boolean }>`
  flex: 1;
  color: ${props => props.$selected ? '#ffffff' : 'rgba(255, 255, 255, 0.8)'};
`;

const ControlsBottom = styled.div`
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px;
  background: rgba(1, 22, 46, 0.8);
  backdrop-filter: blur(10px);
  border-top: 1px solid rgba(1, 58, 99, 0.5);
  box-shadow: 0 -2px 10px rgba(0, 0, 0, 0.3);
`;

const PositionSlider = styled.input`
  flex: 1;
  height: 6px;
  background: rgba(0, 39, 70, 0.5);
  border-radius: 3px;
  outline: none;
  -webkit-appearance: none;

  &::-webkit-slider-thumb {
    -webkit-appearance: none;
    appearance: none;
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #003A63 0%, #01426D 100%);
    border-radius: 50%;
    cursor: pointer;
    box-shadow: 0 2px 6px rgba(1, 66, 109, 0.6);
  }

  &::-moz-range-thumb {
    width: 16px;
    height: 16px;
    background: linear-gradient(135deg, #003A63 0%, #01426D 100%);
    border-radius: 50%;
    cursor: pointer;
    border: none;
    box-shadow: 0 2px 6px rgba(1, 66, 109, 0.6);
  }
`;

const TimeLabel = styled.div`
  min-width: 120px;
  text-align: center;
  font-family: 'Courier New', monospace;
  font-size: 14px;
  color: #ffffff;
  font-weight: 500;
`;

const CheckboxLabel = styled.label`
  display: flex;
  align-items: center;
  gap: 8px;
  cursor: pointer;
  user-select: none;
  color: #ffffff;
  font-weight: 500;
`;

const Checkbox = styled.input`
  width: 18px;
  height: 18px;
  cursor: pointer;
`;

const TagLabelInput = styled.input`
  padding: 6px 10px;
  background: rgba(0, 39, 70, 0.8);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 6px;
  font-size: 14px;
  flex: 1;
  min-width: 200px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #01426D;
    box-shadow: 0 0 0 3px rgba(1, 66, 109, 0.3);
    background: rgba(0, 39, 70, 1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const TabsContainer = styled.div`
  display: flex;
  gap: 4px;
  margin-bottom: 10px;
  border-bottom: 2px solid rgba(1, 66, 109, 0.5);
`;

const Tab = styled.button<{ $active?: boolean; $isAdd?: boolean }>`
  padding: 8px 16px;
  background: ${props => {
    if (props.$isAdd) {
      return 'rgba(0, 58, 99, 0.4)';
    }
    return props.$active 
      ? 'linear-gradient(135deg, #003A63 0%, #01426D 100%)' 
      : 'transparent';
  }};
  color: ${props => {
    if (props.$isAdd) return '#5FA8D3';
    return props.$active ? '#ffffff' : 'rgba(255, 255, 255, 0.7)';
  }};
  border: none;
  border-bottom: 2px solid ${props => props.$active ? '#01426D' : 'transparent'};
  cursor: pointer;
  font-size: 14px;
  font-weight: ${props => props.$active ? '600' : '400'};
  transition: all 0.3s ease;
  border-radius: 8px 8px 0 0;

  &:hover {
    background: ${props => {
      if (props.$isAdd) {
        return 'rgba(0, 58, 99, 0.6)';
      }
      return props.$active 
        ? 'linear-gradient(135deg, #013155 0%, #003A63 100%)' 
        : 'rgba(0, 58, 99, 0.3)';
    }};
    color: ${props => {
      if (props.$isAdd) return '#7BB8D8';
      return props.$active ? '#ffffff' : '#5FA8D3';
    }};
  }
`;

const RecordingIndicator = styled.div`
  position: absolute;
  top: 10px;
  right: 10px;
  width: 16px;
  height: 16px;
  background: linear-gradient(135deg, #C41E3A 0%, #E63946 100%);
  border-radius: 50%;
  z-index: 20;
  box-shadow: 0 0 10px rgba(196, 30, 58, 0.8);
  animation: pulse 1.5s ease-in-out infinite;
  
  @keyframes pulse {
    0%, 100% {
      opacity: 1;
      transform: scale(1);
    }
    50% {
      opacity: 0.7;
      transform: scale(1.2);
    }
  }
`;

const TranscodeOverlay = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(1, 22, 46, 0.95);
  backdrop-filter: blur(10px);
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  z-index: 20;
  border-radius: 12px;
`;

const TranscodeText = styled.div`
  color: #ffffff;
  font-size: 16px;
  margin-bottom: 10px;
  font-weight: 500;
`;

const TranscodeProgressBar = styled.div`
  width: 80%;
  height: 8px;
  background: rgba(0, 39, 70, 0.5);
  border-radius: 4px;
  overflow: hidden;
`;

const TranscodeProgressFill = styled.div<{ $progress: number }>`
  height: 100%;
  width: ${props => props.$progress}%;
  background: linear-gradient(135deg, #003A63 0%, #01426D 100%);
  transition: width 0.3s ease;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 0 10px;
  font-weight: 600;
  font-size: 18px;
  color: #ffffff;
  
  img {
    width: 32px;
    height: 32px;
  }
`;

const ModalOverlay = styled.div`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.7);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 1000;
  backdrop-filter: blur(5px);
`;

const ModalContent = styled.div`
  background: linear-gradient(135deg, #001D37 0%, #002746 100%);
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 12px;
  padding: 20px;
  min-width: 400px;
  max-width: 600px;
  box-shadow: 0 8px 32px rgba(0, 0, 0, 0.5);
`;

const ModalTitle = styled.h3`
  margin: 0 0 15px 0;
  color: #ffffff;
  font-size: 18px;
`;

const ModalInput = styled.input`
  padding: 8px 12px;
  background: rgba(0, 39, 70, 0.8);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;
  transition: all 0.2s;

  &:focus {
    outline: none;
    border-color: #01426D;
    box-shadow: 0 0 0 3px rgba(1, 66, 109, 0.3);
    background: rgba(0, 39, 70, 1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

const ModalButton = styled.button`
  padding: 8px 16px;
  background: linear-gradient(135deg, #003A63 0%, #01426D 100%);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 6px;
  cursor: pointer;
  font-size: 14px;
  font-weight: 500;
  transition: all 0.3s ease;
  margin-right: 10px;

  &:hover {
    background: linear-gradient(135deg, #013155 0%, #003A63 100%);
    transform: translateY(-1px);
    box-shadow: 0 4px 12px rgba(1, 66, 109, 0.5);
  }

  &:active {
    transform: translateY(0);
  }
`;

const ParametersList = styled.div`
  margin: 15px 0;
  max-height: 200px;
  overflow-y: auto;
`;

const ParameterItem = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 8px;
  background: rgba(0, 39, 70, 0.5);
  border-radius: 6px;
  margin-bottom: 8px;
  border: 1px solid rgba(1, 66, 109, 0.3);
`;

const ParameterText = styled.span`
  color: #ffffff;
  font-size: 14px;
  flex: 1;
`;

const DeleteButton = styled.button`
  padding: 4px 8px;
  background: rgba(196, 30, 58, 0.3);
  color: #ffffff;
  border: 1px solid rgba(196, 30, 58, 0.5);
  border-radius: 4px;
  cursor: pointer;
  font-size: 12px;
  transition: all 0.2s;

  &:hover {
    background: rgba(196, 30, 58, 0.5);
  }
`;

const TagParameters = styled.div`
  margin-top: 4px;
  font-size: 11px;
  color: rgba(255, 255, 255, 0.6);
  display: flex;
  flex-wrap: wrap;
  gap: 4px;
`;

const ParameterBadge = styled.span`
  background: rgba(0, 58, 99, 0.4);
  padding: 2px 6px;
  border-radius: 4px;
  border: 1px solid rgba(1, 66, 109, 0.3);
`;

const AddParameterButton = styled.button`
  padding: 4px 8px;
  background: rgba(0, 58, 99, 0.3);
  color: #5FA8D3;
  border: 1px solid rgba(1, 66, 109, 0.3);
  border-radius: 4px;
  cursor: pointer;
  font-size: 11px;
  margin-left: 8px;
  transition: all 0.2s;

  &:hover {
    background: rgba(0, 58, 99, 0.5);
    color: #7BB8D8;
  }
`;

const ModalSelect = styled.select`
  padding: 8px 12px;
  background: rgba(0, 39, 70, 0.8);
  color: #ffffff;
  border: 1px solid rgba(1, 66, 109, 0.5);
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;
  transition: all 0.2s;
  cursor: pointer;

  &:focus {
    outline: none;
    border-color: #01426D;
    box-shadow: 0 0 0 3px rgba(1, 66, 109, 0.3);
    background: rgba(0, 39, 70, 1);
  }

  option {
    background: #001D37;
    color: #ffffff;
  }
`;

const KeyBindingInput = styled.input`
  padding: 8px 12px;
  background: rgba(0, 39, 70, 0.8);
  color: #ffffff;
  border: 1px solid ${props => props.readOnly ? 'rgba(1, 66, 109, 0.3)' : 'rgba(1, 66, 109, 0.5)'};
  border-radius: 6px;
  font-size: 14px;
  width: 100%;
  margin-bottom: 10px;
  transition: all 0.2s;
  cursor: ${props => props.readOnly ? 'pointer' : 'text'};

  &:focus {
    outline: none;
    border-color: #01426D;
    box-shadow: 0 0 0 3px rgba(1, 66, 109, 0.3);
    background: rgba(0, 39, 70, 1);
  }

  &::placeholder {
    color: rgba(255, 255, 255, 0.5);
  }
`;

function App() {
  // État des vidéos
  const [video1File, setVideo1File] = useState<File | null>(null);
  const [video2File, setVideo2File] = useState<File | null>(null);
  const [video1Url, setVideo1Url] = useState<string | null>(null);
  const [video2Url, setVideo2Url] = useState<string | null>(null);
  const [video1Fps, setVideo1Fps] = useState<number | null>(null);
  const [video2Fps, setVideo2Fps] = useState<number | null>(null);
  
  // Références aux éléments vidéo
  const video1Ref = useRef<HTMLVideoElement>(null);
  const video2Ref = useRef<HTMLVideoElement>(null);
  const video1InputRef = useRef<HTMLInputElement>(null);
  const video2InputRef = useRef<HTMLInputElement>(null);
  
  // État de lecture
  const [isPlaying, setIsPlaying] = useState(false);
  const [playbackSpeed, setPlaybackSpeed] = useState(1.0);
  const [currentPosition, setCurrentPosition] = useState(0);
  const [maxDuration, setMaxDuration] = useState(0);
  
  // Tags
  const [sequences, setSequences] = useState<Tag[]>([]);
  const [labels, setLabels] = useState<Tag[]>([]);
  const [customTagTypes, setCustomTagTypes] = useState<Record<string, Tag[]>>({});
  const [selectedSequences, setSelectedSequences] = useState<number[]>([]);
  const [selectedLabels, setSelectedLabels] = useState<number[]>([]);
  const [selectedCustomTags, setSelectedCustomTags] = useState<Record<string, number[]>>({});
  const [activeTab, setActiveTab] = useState<string>('sequences');
  const [isSequenceMode, setIsSequenceMode] = useState(false);
  const [sequenceStart, setSequenceStart] = useState<number | null>(null);
  const [tagLabel, setTagLabel] = useState<string>('');
  const [lastParameterKey, setLastParameterKey] = useState<string>('');
  const [editingParameters, setEditingParameters] = useState<{ tab: string; index: number } | null>(null);
  const [parameterKey, setParameterKey] = useState<string>('');
  const [parameterValue, setParameterValue] = useState<string>('');
  
  // Édition de tag (sequenceType et keyBinding) - pour tous les types sauf sequences
  const [editingTag, setEditingTag] = useState<{ tab: string; index: number } | null>(null);
  const [tagSequenceType, setTagSequenceType] = useState<'startSequence' | 'endSequence' | null>(null);
  const [tagKeyBinding, setTagKeyBinding] = useState<string>('');
  const [isCapturingKey, setIsCapturingKey] = useState(false);
  
  // Synchronisation
  const [syncOffsetMs, setSyncOffsetMs] = useState(0);
  const [syncMarker1, setSyncMarker1] = useState<number | null>(null);
  const [syncMarker2, setSyncMarker2] = useState<number | null>(null);
  
  // Transcodage
  const [transcoding1, setTranscoding1] = useState(false);
  const [transcoding2, setTranscoding2] = useState(false);
  const [transcodeProgress1, setTranscodeProgress1] = useState(0);
  const [transcodeProgress2, setTranscodeProgress2] = useState(0);
  
  // Verrou pour éviter les appels multiples simultanés
  const isTogglingRef = useRef(false);

  // Référence pour l'input de chargement JSON
  const jsonInputRef = useRef<HTMLInputElement>(null);

  // Fonction pour sauvegarder les tags dans localStorage
  const saveTagsToLocalStorage = useCallback((videoName: string, sequencesToSave: Tag[], labelsToSave: Tag[], customTypesToSave: Record<string, Tag[]>) => {
    if (!videoName) return;
    try {
      const key = `video_tags_${videoName}`;
      const data = {
        sequences: sequencesToSave,
        labels: labelsToSave,
        ...customTypesToSave
      };
      localStorage.setItem(key, JSON.stringify(data));
    } catch (error) {
      console.error('Erreur lors de la sauvegarde dans localStorage:', error);
    }
  }, []);

  // Fonction pour charger les tags depuis localStorage
  const loadTagsFromLocalStorage = useCallback((videoName: string): { sequences: Tag[], labels: Tag[], customTypes: Record<string, Tag[]> } => {
    if (!videoName) return { sequences: [], labels: [], customTypes: {} };
    try {
      const key = `video_tags_${videoName}`;
      const saved = localStorage.getItem(key);
      if (saved) {
        const parsed = JSON.parse(saved);
        // Support de l'ancien format (juste un tableau de tags)
        if (Array.isArray(parsed)) {
          return { sequences: parsed, labels: [], customTypes: {} };
        }
        // Extraire sequences et labels
        const sequences = parsed.sequences || [];
        const labels = parsed.labels || [];
        // Extraire les types personnalisés (tout ce qui n'est pas sequences, labels, video, video2, sync_offset_ms)
        const customTypes: Record<string, Tag[]> = {};
        Object.keys(parsed).forEach(key => {
          if (key !== 'sequences' && key !== 'labels' && key !== 'video' && key !== 'video2' && key !== 'sync_offset_ms' && Array.isArray(parsed[key])) {
            customTypes[key] = parsed[key];
          }
        });
        return { sequences, labels, customTypes };
      }
    } catch (error) {
      console.error('Erreur lors du chargement depuis localStorage:', error);
    }
    return { sequences: [], labels: [], customTypes: {} };
  }, []);

  // Sauvegarder automatiquement les tags dans localStorage quand ils changent
  useEffect(() => {
    if (video1File) {
      saveTagsToLocalStorage(video1File.name, sequences, labels, customTagTypes);
    }
  }, [sequences, labels, customTagTypes, video1File, saveTagsToLocalStorage]);

  // Nettoyer les URLs d'objets quand les fichiers changent
  useEffect(() => {
    return () => {
      if (video1Url) URL.revokeObjectURL(video1Url);
      if (video2Url) URL.revokeObjectURL(video2Url);
    };
  }, [video1Url, video2Url]);

  const loadVideo1 = async (file: File) => {
    // Nettoyer l'ancienne URL
    if (video1Url) URL.revokeObjectURL(video1Url);
    
    setVideo1File(file);
    setSyncMarker1(null);
    setTranscodeProgress1(0);
    setIsSequenceMode(false);
    setSequenceStart(null);
    
    // Charger les tags sauvegardés depuis localStorage
    const savedData = loadTagsFromLocalStorage(file.name);
    setSequences(savedData.sequences);
    setLabels(savedData.labels);
    setCustomTagTypes(savedData.customTypes);
    
    // Vérifier si la vidéo peut être lue directement
    const canPlay = await canPlayVideo(file);
    
    if (canPlay) {
      // La vidéo peut être lue directement
      const url = URL.createObjectURL(file);
      setVideo1Url(url);
      setVideo1Fps(30); // Par défaut
    } else {
      // Transcoder la vidéo
      setTranscoding1(true);
      setTranscodeProgress1(0);
      try {
        const transcodedBlob = await transcodeVideo(file, (progress) => {
          setTranscodeProgress1(progress);
        });
        const url = URL.createObjectURL(transcodedBlob);
        setVideo1Url(url);
        setVideo1Fps(30);
        setTranscodeProgress1(100);
        // Attendre un peu avant de masquer l'overlay
        setTimeout(() => {
          setTranscoding1(false);
        }, 500);
      } catch (error) {
        console.error('Failed to transcode video 1:', error);
        alert(`Erreur lors du transcodage de la vidéo 1: ${error instanceof Error ? error.message : 'Format non supporté'}`);
        setTranscoding1(false);
        setTranscodeProgress1(0);
      }
    }
  };

  const loadVideo2 = async (file: File) => {
    // Nettoyer l'ancienne URL
    if (video2Url) URL.revokeObjectURL(video2Url);
    
    setVideo2File(file);
    setSyncMarker2(null);
    setTranscodeProgress2(0);
    
    // Vérifier si la vidéo peut être lue directement
    const canPlay = await canPlayVideo(file);
    
    if (canPlay) {
      // La vidéo peut être lue directement
      const url = URL.createObjectURL(file);
      setVideo2Url(url);
      setVideo2Fps(30); // Par défaut
    } else {
      // Transcoder la vidéo
      setTranscoding2(true);
      setTranscodeProgress2(0);
      try {
        const transcodedBlob = await transcodeVideo(file, (progress) => {
          setTranscodeProgress2(progress);
        });
        const url = URL.createObjectURL(transcodedBlob);
        setVideo2Url(url);
        setVideo2Fps(30);
        setTranscodeProgress2(100);
        // Attendre un peu avant de masquer l'overlay
        setTimeout(() => {
          setTranscoding2(false);
        }, 500);
      } catch (error) {
        console.error('Failed to transcode video 2:', error);
        alert(`Erreur lors du transcodage de la vidéo 2: ${error instanceof Error ? error.message : 'Format non supporté'}`);
        setTranscoding2(false);
        setTranscodeProgress2(0);
      }
    }
  };

  const updateMaxDuration = () => {
    const duration1 = video1Ref.current?.duration || 0;
    const duration2 = video2Ref.current?.duration || 0;
    const max = Math.max(duration1, duration2) * 1000; // Convertir en ms
    setMaxDuration(max);
  };

  useEffect(() => {
    const video1 = video1Ref.current;
    const video2 = video2Ref.current;
    
    const handleLoadedMetadata = () => {
      updateMaxDuration();
    };
    
    if (video1) {
      video1.addEventListener('loadedmetadata', handleLoadedMetadata);
    }
    if (video2) {
      video2.addEventListener('loadedmetadata', handleLoadedMetadata);
    }
    
    return () => {
      if (video1) video1.removeEventListener('loadedmetadata', handleLoadedMetadata);
      if (video2) video2.removeEventListener('loadedmetadata', handleLoadedMetadata);
    };
  }, [video1Url, video2Url]);

  // Gestion de la lecture
  const togglePlayPause = async () => {
    if (!video1Ref.current) return;
    
    // Empêcher les appels multiples simultanés
    if (isTogglingRef.current) {
      return;
    }
    
    isTogglingRef.current = true;
    
    try {
      if (isPlaying) {
        // Pause les deux vidéos simultanément
        // Utiliser requestAnimationFrame pour synchroniser parfaitement
        requestAnimationFrame(() => {
          if (video1Ref.current) video1Ref.current.pause();
          if (video2Ref.current) video2Ref.current.pause();
        });
        setIsPlaying(false);
        // Libérer le verrou immédiatement pour le pause
        isTogglingRef.current = false;
        return;
      }
      
      // Pour le play, on ne repositionne que si nécessaire
      const targetTime1 = currentPosition / 1000;
      const currentTime1 = video1Ref.current.currentTime;
      const timeDiff1 = Math.abs(currentTime1 - targetTime1);
      
      // Repositionner la vidéo 1 seulement si la différence est significative (> 0.1s)
      if (timeDiff1 > 0.1) {
        const duration1 = video1Ref.current.duration || 0;
        if (duration1 > 0) {
          const clampedTime1 = Math.min(targetTime1, duration1);
          video1Ref.current.currentTime = clampedTime1;
        }
      }
      
      // Repositionner la vidéo 2 si nécessaire
      if (video2Ref.current) {
        const duration2 = video2Ref.current.duration || 0;
        if (duration2 > 0) {
          const targetTime2 = (currentPosition + syncOffsetMs) / 1000;
          const currentTime2 = video2Ref.current.currentTime;
          const timeDiff2 = Math.abs(currentTime2 - targetTime2);
          
          if (timeDiff2 > 0.1) {
            const clampedTime2 = Math.max(0, Math.min(targetTime2, duration2));
            video2Ref.current.currentTime = clampedTime2;
          }
        }
      }
      
      // Attendre un court instant pour que les vidéos se positionnent si nécessaire
      if (timeDiff1 > 0.1 || (video2Ref.current && Math.abs((video2Ref.current.currentTime - (currentPosition + syncOffsetMs) / 1000)) > 0.1)) {
        await new Promise(resolve => setTimeout(resolve, 50));
      }
      
      // Lancer les deux vidéos en même temps avec synchronisation parfaite
      // Utiliser requestAnimationFrame pour démarrer exactement au même moment
      await new Promise<void>((resolve) => {
        requestAnimationFrame(() => {
          const playPromises: Promise<void>[] = [];
          
          // Démarrer les deux vidéos dans le même frame
          if (video1Ref.current) {
            try {
              const promise1 = video1Ref.current.play();
              if (promise1) {
                playPromises.push(promise1);
              }
            } catch (err) {
              console.error('Error playing video 1:', err);
            }
          }
          
          if (video2Ref.current) {
            try {
              const promise2 = video2Ref.current.play();
              if (promise2) {
                playPromises.push(promise2);
              }
            } catch (err) {
              console.error('Error playing video 2:', err);
            }
          }
          
          // Attendre que les deux vidéos démarrent
          Promise.all(playPromises)
            .then(() => {
              setIsPlaying(true);
              resolve();
            })
            .catch((error) => {
              console.error('Error starting videos:', error);
              // Vérifier l'état réel des vidéos
              const video1Playing = video1Ref.current ? !video1Ref.current.paused : false;
              const video2Playing = video2Ref.current ? !video2Ref.current.paused : false;
              setIsPlaying(video1Playing || video2Playing);
              resolve();
            });
        });
      });
    } finally {
      // Libérer le verrou après un court délai
      setTimeout(() => {
        isTogglingRef.current = false;
      }, 50);
    }
  };

  const togglePlaybackSpeed = () => {
    const newSpeed = playbackSpeed === 1.0 ? 0.25 : 1.0;
    setPlaybackSpeed(newSpeed);
    if (video1Ref.current) video1Ref.current.playbackRate = newSpeed;
    if (video2Ref.current) video2Ref.current.playbackRate = newSpeed;
  };

  const seek = (position: number) => {
    const clampedPosition = Math.max(0, Math.min(position, maxDuration));
    setCurrentPosition(clampedPosition);
    
    // Positionner la vidéo 1
    if (video1Ref.current) {
      const duration1 = (video1Ref.current.duration || 0) * 1000;
      if (clampedPosition <= duration1) {
        video1Ref.current.currentTime = clampedPosition / 1000;
      }
    }
    
    // Positionner la vidéo 2 avec offset de synchronisation
    if (video2Ref.current) {
      const duration2 = (video2Ref.current.duration || 0) * 1000;
      // Appliquer l'offset de synchronisation
      const targetTime2 = (clampedPosition + syncOffsetMs) / 1000;
      const clampedTime2 = Math.max(0, Math.min(targetTime2, duration2));
      video2Ref.current.currentTime = clampedTime2;
    }
  };

  const handleVideo1TimeUpdate = () => {
    if (video1Ref.current && !video1Ref.current.seeking && isPlaying) {
      const position = video1Ref.current.currentTime * 1000;
      const duration1 = (video1Ref.current.duration || 0) * 1000;
      if (position <= duration1) {
        setCurrentPosition(position);
        
        // Synchroniser la vidéo 2 en temps réel pendant la lecture
        if (video2Ref.current && !video2Ref.current.seeking && !video2Ref.current.paused) {
          const duration2 = (video2Ref.current.duration || 0) * 1000;
          const targetTime2 = (position + syncOffsetMs) / 1000;
          const currentTime2 = video2Ref.current.currentTime;
          const expectedTime2 = Math.max(0, Math.min(targetTime2, duration2));
          
          // Synchroniser seulement si la différence est significative (> 50ms)
          // pour éviter les micro-ajustements constants qui peuvent causer des saccades
          if (Math.abs(currentTime2 - expectedTime2) > 0.05) {
            video2Ref.current.currentTime = expectedTime2;
          }
        }
      }
    }
  };

  // Navigation
  const navigate = (offsetMs: number) => {
    seek(currentPosition + offsetMs);
  };

  const navigateOneFrame = (forward: boolean) => {
    const fps = video1Fps || video2Fps || 30;
    const frameDurationMs = 1000 / fps;
    navigate(forward ? frameDurationMs : -frameDurationMs);
  };

  // Tags
  const addTag = () => {
    if (!video1File) {
      alert('Ouvrez d\'abord une vidéo');
      return;
    }

    const currentTimeMs = Math.floor(currentPosition);
    const timecode = msToTimecode(currentPosition);

    // Utiliser le label du champ texte, ou un label par défaut si vide
    const labelToUse = tagLabel.trim();

    if (activeTab === 'sequences') {
      // Mode séquence
      if (isSequenceMode) {
        if (sequenceStart === null) {
          // Premier tag = début de séquence
          setSequenceStart(currentTimeMs);
        } else {
          // Deuxième tag = fin de séquence
          const startTimeMs = sequenceStart;
          const endTimeMs = currentTimeMs;
          const startTimecode = msToTimecode(startTimeMs);
          const endTimecode = msToTimecode(endTimeMs);
          
          const finalLabel = labelToUse || `Séquence ${startTimecode} - ${endTimecode}`;
          
          const newTag: Tag = {
            time_ms: [startTimeMs, endTimeMs],
            timecode: `${startTimecode} - ${endTimecode}`,
            label: finalLabel
          };
          
          setSequences([...sequences, newTag].sort((a, b) => {
            const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
            const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
            return aTime - bTime;
          }));
          
          // Réinitialiser la séquence
          setSequenceStart(null);
        }
      } else {
        // Tag simple (séquence)
        const finalLabel = labelToUse || `Séquence @ ${timecode}`;
        
        const newTag: Tag = {
          time_ms: currentTimeMs,
          timecode,
          label: finalLabel
        };
        
        setSequences([...sequences, newTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        }));
      }
    } else if (activeTab === 'labels') {
      // Mode label
      const finalLabel = labelToUse || `Label @ ${timecode}`;
      
      const newTag: Tag = {
        time_ms: currentTimeMs,
        timecode,
        label: finalLabel
      };
      
      setLabels([...labels, newTag].sort((a, b) => {
        const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
        const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
        return aTime - bTime;
      }));
    } else {
      // Type personnalisé
      const finalLabel = labelToUse || `${activeTab} @ ${timecode}`;
      
      const newTag: Tag = {
        time_ms: currentTimeMs,
        timecode,
        label: finalLabel
      };
      
      setCustomTagTypes(prev => {
        const currentTags = prev[activeTab] || [];
        const updatedTags = [...currentTags, newTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        });
        return { ...prev, [activeTab]: updatedTags };
      });
    }
  };

  const addTagWithLabel = () => {
    // Cette fonction est maintenant identique à addTag car on utilise le champ texte
    addTag();
  };

  const deleteSelectedTags = () => {
    if (activeTab === 'sequences') {
      const newSequences = sequences.filter((_, index) => !selectedSequences.includes(index));
      setSequences(newSequences);
      setSelectedSequences([]);
    } else if (activeTab === 'labels') {
      const newLabels = labels.filter((_, index) => !selectedLabels.includes(index));
      setLabels(newLabels);
      setSelectedLabels([]);
    } else {
      // Type personnalisé
      const currentTags = customTagTypes[activeTab] || [];
      const selectedIndices = selectedCustomTags[activeTab] || [];
      const newTags = currentTags.filter((_, index) => !selectedIndices.includes(index));
      setCustomTagTypes(prev => ({ ...prev, [activeTab]: newTags }));
      setSelectedCustomTags(prev => ({ ...prev, [activeTab]: [] }));
    }
  };

  const goToTag = (tag: Tag) => {
    if (Array.isArray(tag.time_ms)) {
      // Pour une séquence, aller au début
      seek(tag.time_ms[0]);
    } else {
      seek(tag.time_ms);
    }
  };

  const calculateOffsetFromTags = () => {
    let selectedTags: number[];
    let currentTags: Tag[];
    
    if (activeTab === 'sequences') {
      selectedTags = selectedSequences;
      currentTags = sequences;
    } else if (activeTab === 'labels') {
      selectedTags = selectedLabels;
      currentTags = labels;
    } else {
      selectedTags = selectedCustomTags[activeTab] || [];
      currentTags = customTagTypes[activeTab] || [];
    }
    
    if (selectedTags.length !== 2) {
      alert('Sélectionnez exactement 2 tags pour calculer l\'offset.\nUtilisez Ctrl+clic pour sélectionner plusieurs tags.');
      return;
    }

    const tag1 = currentTags[selectedTags[0]];
    const tag2 = currentTags[selectedTags[1]];
    
    // Extraire le temps (début pour les séquences)
    const time1 = Array.isArray(tag1.time_ms) ? tag1.time_ms[0] : tag1.time_ms;
    const time2 = Array.isArray(tag2.time_ms) ? tag2.time_ms[0] : tag2.time_ms;
    const offset = time2 - time1;
    
    setSyncOffsetMs(offset);
    alert(`Offset calculé: ${offset} ms (${(offset / 1000).toFixed(2)} s)`);
  };

  // Fonction pour créer un nouveau type de tag
  const createNewTagType = () => {
    const typeName = window.prompt('Nom du nouveau type de tag:');
    if (!typeName || !typeName.trim()) {
      return;
    }
    
    const trimmedName = typeName.trim();
    
    // Vérifier que le nom n'existe pas déjà
    if (trimmedName === 'sequences' || trimmedName === 'labels' || customTagTypes[trimmedName]) {
      alert('Ce nom est déjà utilisé. Veuillez choisir un autre nom.');
      return;
    }
    
    // Créer le nouveau type
    setCustomTagTypes(prev => ({ ...prev, [trimmedName]: [] }));
    setSelectedCustomTags(prev => ({ ...prev, [trimmedName]: [] }));
    setActiveTab(trimmedName);
  };

  // Fonction pour ouvrir la modale d'édition de paramètres
  const openParameterEditor = (tab: string, index: number) => {
    setEditingParameters({ tab, index });
    // Pré-remplir avec la dernière clé utilisée
    setParameterKey(lastParameterKey);
    setParameterValue('');
  };

  // Fonction pour ajouter un paramètre
  const addParameter = () => {
    if (!editingParameters) return;
    
    const key = parameterKey.trim();
    const value = parameterValue.trim();
    
    if (!key) {
      alert('Veuillez entrer une clé');
      return;
    }
    
    // Sauvegarder la clé pour la prochaine fois
    setLastParameterKey(key);
    
    // Mettre à jour le tag avec le nouveau paramètre
    if (editingParameters.tab === 'sequences') {
      const tag = sequences[editingParameters.index];
      const updatedTag: Tag = {
        ...tag,
        parameters: {
          ...(tag.parameters || {}),
          [key]: value
        }
      };
      const newSequences = [...sequences];
      newSequences[editingParameters.index] = updatedTag;
      setSequences(newSequences);
    } else if (editingParameters.tab === 'labels') {
      const tag = labels[editingParameters.index];
      const updatedTag: Tag = {
        ...tag,
        parameters: {
          ...(tag.parameters || {}),
          [key]: value
        }
      };
      const newLabels = [...labels];
      newLabels[editingParameters.index] = updatedTag;
      setLabels(newLabels);
    } else {
      const tag = customTagTypes[editingParameters.tab]?.[editingParameters.index];
      if (tag) {
        const updatedTag: Tag = {
          ...tag,
          parameters: {
            ...(tag.parameters || {}),
            [key]: value
          }
        };
        const newTags = [...(customTagTypes[editingParameters.tab] || [])];
        newTags[editingParameters.index] = updatedTag;
        setCustomTagTypes(prev => ({ ...prev, [editingParameters.tab]: newTags }));
      }
    }
    
    // Réinitialiser la valeur mais garder la clé
    setParameterValue('');
  };

  // Fonction pour supprimer un paramètre
  const removeParameter = (key: string) => {
    if (!editingParameters) return;
    
    if (editingParameters.tab === 'sequences') {
      const tag = sequences[editingParameters.index];
      const updatedParameters = { ...(tag.parameters || {}) };
      delete updatedParameters[key];
      const updatedTag: Tag = {
        ...tag,
        parameters: Object.keys(updatedParameters).length > 0 ? updatedParameters : undefined
      };
      const newSequences = [...sequences];
      newSequences[editingParameters.index] = updatedTag;
      setSequences(newSequences);
    } else if (editingParameters.tab === 'labels') {
      const tag = labels[editingParameters.index];
      const updatedParameters = { ...(tag.parameters || {}) };
      delete updatedParameters[key];
      const updatedTag: Tag = {
        ...tag,
        parameters: Object.keys(updatedParameters).length > 0 ? updatedParameters : undefined
      };
      const newLabels = [...labels];
      newLabels[editingParameters.index] = updatedTag;
      setLabels(newLabels);
    } else {
      const tag = customTagTypes[editingParameters.tab]?.[editingParameters.index];
      if (tag) {
        const updatedParameters = { ...(tag.parameters || {}) };
        delete updatedParameters[key];
        const updatedTag: Tag = {
          ...tag,
          parameters: Object.keys(updatedParameters).length > 0 ? updatedParameters : undefined
        };
        const newTags = [...(customTagTypes[editingParameters.tab] || [])];
        newTags[editingParameters.index] = updatedTag;
        setCustomTagTypes(prev => ({ ...prev, [editingParameters.tab]: newTags }));
      }
    }
  };

  // Fonction pour fermer la modale
  const closeParameterEditor = () => {
    setEditingParameters(null);
    setParameterKey('');
    setParameterValue('');
  };

  // Synchronisation
  const handleSetSyncMarker1 = () => {
    if (!video1File) {
      alert('Ouvrez d\'abord la vidéo 1');
      return;
    }
    const newMarker1 = currentPosition;
    setSyncMarker1(newMarker1);
    
    if (syncMarker2 !== null) {
      const offset = syncMarker2 - newMarker1;
      setSyncOffsetMs(offset);
      alert(`Offset calculé: ${offset} ms (${(offset / 1000).toFixed(2)} s)`);
    }
  };

  const handleSetSyncMarker2 = () => {
    if (!video2File) {
      alert('Ouvrez d\'abord la vidéo 2');
      return;
    }
    const newMarker2 = currentPosition;
    setSyncMarker2(newMarker2);
    
    if (syncMarker1 !== null) {
      const offset = newMarker2 - syncMarker1;
      setSyncOffsetMs(offset);
      alert(`Offset calculé: ${offset} ms (${(offset / 1000).toFixed(2)} s)`);
    }
  };

  // Export JSON
  const exportTags = () => {
    if (!video1File) {
      alert('Ouvrez d\'abord une vidéo');
      return;
    }

    const totalTags = sequences.length + labels.length + Object.values(customTagTypes).reduce((sum, tags) => sum + tags.length, 0);
    if (totalTags === 0) {
      alert('Aucun tag à exporter');
      return;
    }

    const tagData: TagData = {
      video: video1File.name,
      video2: video2File?.name || null,
      sync_offset_ms: syncOffsetMs,
      sequences,
      labels,
      ...customTagTypes
    };

    // Créer et télécharger le fichier JSON
    const jsonStr = JSON.stringify(tagData, null, 2);
    const blob = new Blob([jsonStr], { type: 'application/json' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${video1File.name.replace(/\.[^/.]+$/, '')}_tags.json`;
    document.body.appendChild(a);
    a.click();
    document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  // Charger les tags depuis un fichier JSON
  const loadTagsFromJson = () => {
    if (!video1File) {
      alert('Ouvrez d\'abord une vidéo');
      return;
    }

    jsonInputRef.current?.click();
  };

  const handleJsonFileLoad = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;

    const reader = new FileReader();
    reader.onload = (event) => {
      try {
        const content = event.target?.result as string;
        const parsed = JSON.parse(content);
        
        // Support de l'ancien format (avec "tags")
        let tagData: TagData;
        if (parsed.tags && !parsed.sequences) {
          // Ancien format : convertir tags en sequences
          tagData = {
            video: parsed.video || null,
            video2: parsed.video2 || null,
            sync_offset_ms: parsed.sync_offset_ms || 0,
            sequences: parsed.tags || [],
            labels: []
          };
        } else {
          // Nouveau format
          tagData = {
            video: parsed.video || null,
            video2: parsed.video2 || null,
            sync_offset_ms: parsed.sync_offset_ms || 0,
            sequences: parsed.sequences || [],
            labels: parsed.labels || []
          };
        }
        
        // Extraire les types personnalisés (tout ce qui n'est pas sequences, labels, video, video2, sync_offset_ms)
        const customTypes: Record<string, Tag[]> = {};
        Object.keys(parsed).forEach(key => {
          if (key !== 'sequences' && key !== 'labels' && key !== 'video' && key !== 'video2' && key !== 'sync_offset_ms' && Array.isArray(parsed[key])) {
            customTypes[key] = parsed[key];
          }
        });
        
        // Vérifier que les tags correspondent à la vidéo actuelle
        if (tagData.video && tagData.video !== video1File?.name) {
          const confirmLoad = window.confirm(
            `Les tags dans ce fichier sont pour la vidéo "${tagData.video}"\n` +
            `mais vous avez chargé "${video1File?.name}".\n` +
            `Voulez-vous quand même charger ces tags ?`
          );
          if (!confirmLoad) {
            e.target.value = '';
            return;
          }
        }

        // Charger les tags
        setSequences(tagData.sequences);
        setLabels(tagData.labels);
        setCustomTagTypes(customTypes);
        
        // Initialiser les sélections pour les nouveaux types
        const newSelections: Record<string, number[]> = {};
        Object.keys(customTypes).forEach(key => {
          newSelections[key] = [];
        });
        setSelectedCustomTags(newSelections);
        
        // Optionnellement charger l'offset de synchronisation
        if (tagData.sync_offset_ms !== undefined) {
          setSyncOffsetMs(tagData.sync_offset_ms);
        }

        const customTagsCount = Object.values(customTypes).reduce((sum, tags) => sum + tags.length, 0);
        const totalTags = tagData.sequences.length + tagData.labels.length + customTagsCount;
        const customTypesNames = Object.keys(customTypes).join(', ');
        const customTypesInfo = customTypesNames ? `, ${customTagsCount} dans les types personnalisés (${customTypesNames})` : '';
        alert(`${totalTags} tag(s) chargé(s) avec succès (${tagData.sequences.length} séquence(s), ${tagData.labels.length} label(s)${customTypesInfo})`);
      } catch (error) {
        console.error('Erreur lors du chargement du JSON:', error);
        alert('Erreur lors du chargement du fichier JSON. Vérifiez que le fichier est valide.');
      }
    };
    reader.onerror = () => {
      alert('Erreur lors de la lecture du fichier');
    };
    reader.readAsText(file);
    
    // Réinitialiser l'input pour permettre de recharger le même fichier
    e.target.value = '';
  };

  // Fonction pour ouvrir l'éditeur de tag (pour tous les types sauf sequences)
  const openTagEditor = (tab: string, index: number) => {
    if (tab === 'sequences') return; // Ne pas éditer les séquences
    
    let tag: Tag | undefined;
    if (tab === 'labels') {
      tag = labels[index];
    } else {
      tag = customTagTypes[tab]?.[index];
    }
    
    if (!tag) return;
    
    setEditingTag({ tab, index });
    setTagSequenceType(tag.sequenceType || null);
    setTagKeyBinding(tag.keyBinding || '');
    setIsCapturingKey(false);
  };

  // Fonction pour fermer l'éditeur de tag
  const closeTagEditor = () => {
    setEditingTag(null);
    setTagSequenceType(null);
    setTagKeyBinding('');
    setIsCapturingKey(false);
  };

  // Fonction pour sauvegarder les modifications d'un tag
  const saveTagEdit = () => {
    if (editingTag === null) return;
    
    const { tab, index } = editingTag;
    
    if (tab === 'labels') {
      const tag = labels[index];
      const updatedTag: Tag = {
        ...tag,
        sequenceType: tagSequenceType,
        keyBinding: tagKeyBinding || undefined
      };
      
      const newLabels = [...labels];
      newLabels[index] = updatedTag;
      setLabels(newLabels);
    } else {
      const tag = customTagTypes[tab]?.[index];
      if (!tag) return;
      
      const updatedTag: Tag = {
        ...tag,
        sequenceType: tagSequenceType,
        keyBinding: tagKeyBinding || undefined
      };
      
      const newTags = [...(customTagTypes[tab] || [])];
      newTags[index] = updatedTag;
      setCustomTagTypes(prev => ({ ...prev, [tab]: newTags }));
    }
    
    closeTagEditor();
  };

  // Fonction pour régénérer les séquences à partir de tous les tags startSequence/endSequence (sauf sequences)
  const regenerateSequencesFromLabels = () => {
    if (!video1File) {
      alert('Ouvrez d\'abord une vidéo');
      return;
    }

    // Collecter tous les tags de tous les types (sauf sequences)
    const allTags: Tag[] = [];
    
    // Ajouter les labels
    allTags.push(...labels);
    
    // Ajouter les tags personnalisés
    Object.values(customTagTypes).forEach(tags => {
      allTags.push(...tags);
    });

    // Trier tous les tags par temps
    const sortedTags = allTags.sort((a, b) => {
      const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
      const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
      return aTime - bTime;
    });

    const newSequences: Tag[] = [];
    let currentStart: Tag | null = null;

    for (const tag of sortedTags) {
      if (tag.sequenceType === 'startSequence') {
        // Si on a déjà un start en cours, on l'ignore (on prend le plus récent)
        currentStart = tag;
      } else if (tag.sequenceType === 'endSequence' && currentStart !== null) {
        // Créer une séquence
        const startTime = Array.isArray(currentStart.time_ms) ? currentStart.time_ms[0] : currentStart.time_ms;
        const endTime = Array.isArray(tag.time_ms) ? tag.time_ms[0] : tag.time_ms;
        
        if (startTime < endTime) {
          const startTimecode = msToTimecode(startTime);
          const endTimecode = msToTimecode(endTime);
          
          // Utiliser le label du startSequence comme nom de la séquence, ou un nom par défaut
          const sequenceLabel = currentStart.label || `Séquence ${startTimecode} - ${endTimecode}`;
          
          const newSequence: Tag = {
            time_ms: [startTime, endTime],
            timecode: `${startTimecode} - ${endTimecode}`,
            label: sequenceLabel,
            // Copier les paramètres du startSequence si présents
            parameters: currentStart.parameters
          };
          
          newSequences.push(newSequence);
        }
        currentStart = null;
      }
    }

    // Remplacer les séquences existantes
    setSequences(newSequences);
    
    const count = newSequences.length;
    alert(`${count} séquence(s) régénérée(s) à partir des tags startSequence/endSequence.`);
  };

  // Fonction pour appeler un tag via son binding de touche (cherche dans tous les types sauf sequences)
  const triggerLabelByKey = useCallback((key: string) => {
    // Vérifier qu'une vidéo est sélectionnée
    if (!video1File && !video2File) {
      return;
    }

    // Chercher dans tous les types de tags (labels + customTagTypes) sauf sequences
    let tagTemplate: Tag | undefined;
    let tagTemplateTab: string | null = null;
    
    // Chercher dans les labels
    tagTemplate = labels.find(l => l.keyBinding && l.keyBinding.toLowerCase() === key.toLowerCase());
    if (tagTemplate) {
      tagTemplateTab = 'labels';
    } else {
      // Chercher dans les types personnalisés
      for (const [tabName, tags] of Object.entries(customTagTypes)) {
        tagTemplate = tags.find(t => t.keyBinding && t.keyBinding.toLowerCase() === key.toLowerCase());
        if (tagTemplate) {
          tagTemplateTab = tabName;
          break;
        }
      }
    }
    
    if (!tagTemplate || !tagTemplateTab) {
      return;
    }

    // Appeler le tag (créer un tag au temps actuel avec ce template)
    const currentTimeMs = Math.floor(currentPosition);
    const timecode = msToTimecode(currentPosition);
    
    // Si c'est un startSequence ou endSequence, gérer la logique
    if (tagTemplate.sequenceType === 'startSequence') {
      // Créer un tag startSequence au temps actuel dans le même type que le template
      const newTag: Tag = {
        time_ms: currentTimeMs,
        timecode,
        label: tagTemplate.label,
        sequenceType: 'startSequence'
      };
      
      if (tagTemplateTab === 'labels') {
        setLabels(prev => [...prev, newTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        }));
      } else {
        setCustomTagTypes(prev => {
          const currentTags = prev[tagTemplateTab!] || [];
          const updatedTags = [...currentTags, newTag].sort((a, b) => {
            const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
            const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
            return aTime - bTime;
          });
          return { ...prev, [tagTemplateTab!]: updatedTags };
        });
      }
    } else if (tagTemplate.sequenceType === 'endSequence') {
      // Créer d'abord un tag endSequence au temps actuel dans le même type que le template
      const endTag: Tag = {
        time_ms: currentTimeMs,
        timecode,
        label: tagTemplate.label,
        sequenceType: 'endSequence'
      };
      
      // Chercher le dernier startSequence (parmi tous les tags créés de tous types) pour créer la séquence
      // On cherche dans tous les tags existants (pas les templates) le plus récent startSequence
      const allTags: Tag[] = [];
      allTags.push(...labels.filter(l => !l.keyBinding)); // Exclure les templates
      Object.values(customTagTypes).forEach(tags => {
        allTags.push(...tags.filter(t => !t.keyBinding)); // Exclure les templates
      });
      
      const sortedTags = allTags.sort((a, b) => {
        const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
        const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
        return bTime - aTime; // Tri décroissant pour trouver le plus récent
      });
      
      const lastStartTag = sortedTags.find(t => 
        t.sequenceType === 'startSequence' && 
        (Array.isArray(t.time_ms) ? t.time_ms[0] : t.time_ms) < currentTimeMs
      );
      
      // Ajouter le tag endSequence dans le bon type
      if (tagTemplateTab === 'labels') {
        setLabels(prev => [...prev, endTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        }));
      } else {
        setCustomTagTypes(prev => {
          const currentTags = prev[tagTemplateTab!] || [];
          const updatedTags = [...currentTags, endTag].sort((a, b) => {
            const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
            const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
            return aTime - bTime;
          });
          return { ...prev, [tagTemplateTab!]: updatedTags };
        });
      }
      
      // Si on a trouvé un startSequence, créer la séquence
      if (lastStartTag) {
        const startTimeMs = Array.isArray(lastStartTag.time_ms) ? lastStartTag.time_ms[0] : lastStartTag.time_ms;
        const endTimeMs = currentTimeMs;
        const startTimecode = msToTimecode(startTimeMs);
        const endTimecode = msToTimecode(endTimeMs);
        
        const finalLabel = lastStartTag.label || `Séquence ${startTimecode} - ${endTimecode}`;
        
        const newTag: Tag = {
          time_ms: [startTimeMs, endTimeMs],
          timecode: `${startTimecode} - ${endTimecode}`,
          label: finalLabel,
          parameters: lastStartTag.parameters
        };
        
        setSequences(prev => [...prev, newTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        }));
      }
    } else {
      // Tag normal : créer un tag au temps actuel avec le label du template dans le même type
      const newTag: Tag = {
        time_ms: currentTimeMs,
        timecode,
        label: tagTemplate.label
      };
      
      if (tagTemplateTab === 'labels') {
        setLabels(prev => [...prev, newTag].sort((a, b) => {
          const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
          const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
          return aTime - bTime;
        }));
      } else {
        setCustomTagTypes(prev => {
          const currentTags = prev[tagTemplateTab!] || [];
          const updatedTags = [...currentTags, newTag].sort((a, b) => {
            const aTime = Array.isArray(a.time_ms) ? a.time_ms[0] : a.time_ms;
            const bTime = Array.isArray(b.time_ms) ? b.time_ms[0] : b.time_ms;
            return aTime - bTime;
          });
          return { ...prev, [tagTemplateTab!]: updatedTags };
        });
      }
    }
  }, [video1File, video2File, labels, customTagTypes, currentPosition]);

  // Fonction helper pour vérifier si on peut utiliser le raccourci
  const canUseShortcut = (e: KeyboardEvent): boolean => {
    const target = e.target as HTMLElement;
    // Ne pas intercepter si on est dans un input texte, textarea, ou autre élément de saisie
    if (target.tagName === 'INPUT') {
      const inputType = (target as HTMLInputElement).type;
      // Permettre pour les sliders (range) mais pas pour les champs de texte
      if (inputType !== 'range') {
        return false;
      }
    }
    if (target.tagName === 'TEXTAREA') {
      return false;
    }
    // Permettre si on est sur body, sur l'application, ou sur un élément vidéo
    return true;
  };

  // Référence au bouton play/pause pour le déclencher
  const playPauseButtonRef = useRef<HTMLButtonElement>(null);

  // Raccourcis clavier
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      // Vérifier d'abord les bindings de touches pour tous les tags (seulement si une vidéo est sélectionnée)
      if ((video1File || video2File) && !isCapturingKey) {
        const key = e.key.toLowerCase();
        // Chercher dans tous les types de tags (labels + customTagTypes)
        let tagWithBinding: Tag | undefined;
        tagWithBinding = labels.find(l => l.keyBinding && l.keyBinding.toLowerCase() === key);
        if (!tagWithBinding) {
          for (const tags of Object.values(customTagTypes)) {
            tagWithBinding = tags.find(t => t.keyBinding && t.keyBinding.toLowerCase() === key);
            if (tagWithBinding) break;
          }
        }
        if (tagWithBinding && canUseShortcut(e)) {
          e.preventDefault();
          triggerLabelByKey(tagWithBinding.keyBinding!);
          return;
        }
      }

      // Espace : déclencher le bouton play/pause
      if (e.key === ' ' || e.code === 'Space') {
        const target = e.target as HTMLElement;
        // Ne pas intercepter si on est dans un input texte ou textarea
        if (target.tagName === 'INPUT' && (target as HTMLInputElement).type !== 'range') {
          return;
        }
        if (target.tagName === 'TEXTAREA') {
          return;
        }
        // Empêcher le comportement par défaut (scroll)
        e.preventDefault();
        e.stopPropagation();
        // Déclencher le bouton play/pause comme un clic
        if (playPauseButtonRef.current) {
          playPauseButtonRef.current.click();
        }
        return;
      }

      // T : ajouter un tag
      if (e.key === 't' || e.key === 'T') {
        if (canUseShortcut(e)) {
          e.preventDefault();
          addTag();
          return;
        }
      }

      // L : ajouter un tag avec label
      if (e.key === 'l' || e.key === 'L') {
        if (canUseShortcut(e)) {
          e.preventDefault();
          addTagWithLabel();
          return;
        }
      }

      // Suppr/Del : supprimer les tags sélectionnés
      if (e.key === 'Delete' || e.key === 'Backspace') {
        if (canUseShortcut(e)) {
          e.preventDefault();
          deleteSelectedTags();
          return;
        }
      }

      // Ctrl+S : export JSON
      if (e.ctrlKey && e.key === 's') {
        e.preventDefault();
        exportTags();
        return;
      }

      // Flèches gauche/droite : navigation
      if (e.key === 'ArrowLeft' || e.key === 'ArrowRight') {
        if (canUseShortcut(e)) {
          e.preventDefault();
          // Alt ou Cmd (Mac) + flèche : ±1 seconde
          if (e.altKey || e.metaKey) {
            navigate(e.key === 'ArrowLeft' ? -1000 : 1000);
          } else {
            // Flèches seules : frame par frame
            navigateOneFrame(e.key === 'ArrowRight');
          }
          return;
        }
      }

      // PageUp/PageDown : ±2 secondes
      if (e.key === 'PageUp') {
        e.preventDefault();
        navigate(-2000);
      }
      if (e.key === 'PageDown') {
        e.preventDefault();
        navigate(2000);
      }

      // . et , : frame par frame
      if (e.key === '.' || e.key === ',') {
        e.preventDefault();
        navigateOneFrame(e.key === '.');
      }
    };

    window.addEventListener('keydown', handleKeyDown, true); // Utiliser capture phase
    return () => window.removeEventListener('keydown', handleKeyDown, true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [currentPosition, sequences, labels, selectedSequences, selectedLabels, activeTab, video1File, video2File, syncOffsetMs, syncMarker1, syncMarker2, playbackSpeed, isSequenceMode, sequenceStart, tagLabel, isCapturingKey, triggerLabelByKey]);

  // Capture des touches pour le binding
  useEffect(() => {
    if (!isCapturingKey) return;

    const handleKeyCapture = (e: KeyboardEvent) => {
      e.preventDefault();
      e.stopPropagation();
      
      // Ignorer certaines touches spéciales
      if (e.key === 'Escape') {
        setIsCapturingKey(false);
        return;
      }
      
      // Capturer la touche
      const key = e.key;
      setTagKeyBinding(key);
      setIsCapturingKey(false);
    };

    window.addEventListener('keydown', handleKeyCapture, true);
    return () => window.removeEventListener('keydown', handleKeyCapture, true);
  }, [isCapturingKey]);

  return (
    <AppContainer>
      <ControlsTop>
        <LogoContainer>
          <img src="/logo.svg" alt="Origine" />
          <span>Origine</span>
        </LogoContainer>
        <FileInput
          ref={video1InputRef}
          id="video1-input"
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) loadVideo1(file);
          }}
        />
        <FileInputLabel htmlFor="video1-input">
          {video1File ? video1File.name : 'Ouvrir vidéo 1'}
        </FileInputLabel>
        
        <FileInput
          ref={video2InputRef}
          id="video2-input"
          type="file"
          accept="video/*"
          onChange={(e) => {
            const file = e.target.files?.[0];
            if (file) loadVideo2(file);
          }}
        />
        <FileInputLabel htmlFor="video2-input">
          {video2File ? video2File.name : 'Ouvrir vidéo 2'}
        </FileInputLabel>

        <Button ref={playPauseButtonRef} onClick={togglePlayPause}>
          {isPlaying ? 'Pause' : 'Play'}
        </Button>
        
        <Button onClick={togglePlaybackSpeed}>
          Vitesse: {playbackSpeed}x
        </Button>
        
        {(activeTab === 'sequences' || (activeTab !== 'labels' && !customTagTypes[activeTab])) && (
          <CheckboxLabel>
            <Checkbox
              type="checkbox"
              checked={isSequenceMode}
              onChange={(e) => {
                setIsSequenceMode(e.target.checked);
                // Réinitialiser la séquence en cours si on désactive le mode
                if (!e.target.checked) {
                  setSequenceStart(null);
                }
              }}
            />
            Mode séquence
          </CheckboxLabel>
        )}
        
        <TagLabelInput
          type="text"
          placeholder="Label du tag (optionnel)"
          value={tagLabel}
          onChange={(e) => setTagLabel(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === 'Enter') {
              e.preventDefault();
              addTag();
            }
          }}
        />
        
        <Button onClick={addTag}>Tagger (T)</Button>
        
        <Button onClick={calculateOffsetFromTags}>Calculer offset (2 tags)</Button>
        
        <Button onClick={exportTags}>Exporter JSON (Ctrl+S)</Button>
        
        <Button onClick={loadTagsFromJson}>Charger JSON</Button>
        
        {activeTab !== 'sequences' && (
          <Button onClick={regenerateSequencesFromLabels}>
            Régénérer séquences
          </Button>
        )}
        
        <Button 
          onClick={deleteSelectedTags}
          style={{ 
            background: selectedSequences.length > 0 || selectedLabels.length > 0 || (selectedCustomTags[activeTab]?.length || 0) > 0
              ? 'linear-gradient(135deg, #c41e3a 0%, #a01a2e 100%)'
              : undefined
          }}
        >
          Supprimer sélection (Suppr)
        </Button>
        
        <FileInput
          ref={jsonInputRef}
          type="file"
          accept=".json"
          onChange={handleJsonFileLoad}
          style={{ display: 'none' }}
        />
      </ControlsTop>

      <SyncControls>
        <label>
          Offset vidéo 2: {syncOffsetMs} ms
          <OffsetInput
            type="number"
            value={syncOffsetMs}
            onChange={(e) => setSyncOffsetMs(Number(e.target.value))}
          />
        </label>
      </SyncControls>

      <Content>
        <VideosContainer>
          <VideoWrapper>
            <SyncButton
              $active={syncMarker1 !== null}
              onClick={handleSetSyncMarker1}
            >
              {syncMarker1 !== null ? `Sync: ${msToTimecode(syncMarker1)}` : 'Sync'}
            </SyncButton>
            {transcoding1 && (
              <TranscodeOverlay>
                <TranscodeText>Transcodage de la vidéo 1...</TranscodeText>
                <TranscodeProgressBar>
                  <TranscodeProgressFill $progress={transcodeProgress1} />
                </TranscodeProgressBar>
                <TranscodeText style={{ fontSize: '12px', marginTop: '5px' }}>
                  {Math.round(transcodeProgress1)}%
                </TranscodeText>
              </TranscodeOverlay>
            )}
            {sequenceStart !== null && <RecordingIndicator />}
            {video1Url && (
              <VideoPlayer
                ref={video1Ref}
                src={video1Url}
                onTimeUpdate={handleVideo1TimeUpdate}
                onPlay={() => {
                  // Ne pas interférer si on est en train de gérer le toggle
                  if (!isTogglingRef.current) {
                    setIsPlaying(true);
                  }
                }}
                onPause={() => {
                  // Ne pas interférer si on est en train de gérer le toggle
                  if (!isTogglingRef.current) {
                    setIsPlaying(false);
                  }
                }}
                controls={false}
              />
            )}
          </VideoWrapper>
          
          <VideoWrapper>
            <SyncButton
              $active={syncMarker2 !== null}
              onClick={handleSetSyncMarker2}
            >
              {syncMarker2 !== null ? `Sync: ${msToTimecode(syncMarker2)}` : 'Sync'}
            </SyncButton>
            {transcoding2 && (
              <TranscodeOverlay>
                <TranscodeText>Transcodage de la vidéo 2...</TranscodeText>
                <TranscodeProgressBar>
                  <TranscodeProgressFill $progress={transcodeProgress2} />
                </TranscodeProgressBar>
                <TranscodeText style={{ fontSize: '12px', marginTop: '5px' }}>
                  {Math.round(transcodeProgress2)}%
                </TranscodeText>
              </TranscodeOverlay>
            )}
            {video2Url && (
              <VideoPlayer
                ref={video2Ref}
                src={video2Url}
                muted={true}
                onPlay={() => {
                  // Ne pas interférer si on est en train de gérer le toggle
                  if (!isTogglingRef.current) {
                    setIsPlaying(true);
                  }
                }}
                onPause={() => {
                  // Ne pas interférer si on est en train de gérer le toggle
                  if (!isTogglingRef.current) {
                    setIsPlaying(false);
                  }
                }}
                controls={false}
              />
            )}
          </VideoWrapper>
        </VideosContainer>

        <TagsPanel>
          <TabsContainer>
            <Tab
              $active={activeTab === 'sequences'}
              onClick={() => {
                setActiveTab('sequences');
                setSelectedSequences([]);
              }}
            >
              Séquences ({sequences.length})
            </Tab>
            <Tab
              $active={activeTab === 'labels'}
              onClick={() => {
                setActiveTab('labels');
                setSelectedLabels([]);
                // Réinitialiser le mode séquence si on passe en mode labels
                setIsSequenceMode(false);
                setSequenceStart(null);
              }}
            >
              Labels ({labels.length})
            </Tab>
            {Object.keys(customTagTypes).map(typeName => (
              <Tab
                key={typeName}
                $active={activeTab === typeName}
                onClick={() => {
                  setActiveTab(typeName);
                  setSelectedCustomTags(prev => ({ ...prev, [typeName]: [] }));
                  // Réinitialiser le mode séquence si on passe en mode personnalisé
                  setIsSequenceMode(false);
                  setSequenceStart(null);
                }}
              >
                {typeName} ({customTagTypes[typeName].length})
              </Tab>
            ))}
            <Tab
              $isAdd={true}
              onClick={createNewTagType}
            >
              +
            </Tab>
          </TabsContainer>
          <TagsPanelTitle>Ctrl+clic pour sélection multiple</TagsPanelTitle>
          <TagsList>
            {activeTab === 'sequences' ? (
              sequences.map((tag, index) => (
                <TagItem
                  key={index}
                  $selected={selectedSequences.includes(index)}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      setSelectedSequences(prev => 
                        prev.includes(index) 
                          ? prev.filter(i => i !== index)
                          : [...prev, index]
                      );
                    } else {
                      setSelectedSequences([index]);
                    }
                  }}
                  onDoubleClick={() => goToTag(tag)}
                >
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <TagTimecode $selected={selectedSequences.includes(index)}>
                        {tag.timecode}
                      </TagTimecode>
                      <TagLabel $selected={selectedSequences.includes(index)}>
                        {tag.label}
                      </TagLabel>
                      <AddParameterButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openParameterEditor('sequences', index);
                        }}
                      >
                        ⚙️
                      </AddParameterButton>
                    </div>
                    {tag.parameters && Object.keys(tag.parameters).length > 0 && (
                      <TagParameters>
                        {Object.entries(tag.parameters).map(([key, value]) => (
                          <ParameterBadge key={key}>
                            {key}: {value}
                          </ParameterBadge>
                        ))}
                      </TagParameters>
                    )}
                  </div>
                </TagItem>
              ))
            ) : activeTab === 'labels' ? (
              labels.map((tag, index) => (
                <TagItem
                  key={index}
                  $selected={selectedLabels.includes(index)}
                  onClick={(e) => {
                    if (e.ctrlKey || e.metaKey) {
                      setSelectedLabels(prev => 
                        prev.includes(index) 
                          ? prev.filter(i => i !== index)
                          : [...prev, index]
                      );
                    } else {
                      setSelectedLabels([index]);
                    }
                  }}
                  onDoubleClick={() => goToTag(tag)}
                >
                  <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                    <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                      <TagTimecode $selected={selectedLabels.includes(index)}>
                        {tag.timecode}
                      </TagTimecode>
                      <TagLabel $selected={selectedLabels.includes(index)}>
                        {tag.label}
                      </TagLabel>
                      <AddParameterButton
                        onClick={(e) => {
                          e.stopPropagation();
                          openTagEditor('labels', index);
                        }}
                        title="Éditer le tag (sequenceType et binding)"
                      >
                        ⚙️
                      </AddParameterButton>
                    </div>
                    {(tag.sequenceType || tag.keyBinding) && (
                      <TagParameters>
                        {tag.sequenceType && (
                          <ParameterBadge>
                            {tag.sequenceType === 'startSequence' ? 'Début séquence' : 'Fin séquence'}
                          </ParameterBadge>
                        )}
                        {tag.keyBinding && (
                          <ParameterBadge>
                            Touche: {tag.keyBinding}
                          </ParameterBadge>
                        )}
                      </TagParameters>
                    )}
                  </div>
                </TagItem>
              ))
            ) : (
              (customTagTypes[activeTab] || []).map((tag, index) => {
                const selectedIndices = selectedCustomTags[activeTab] || [];
                return (
                  <TagItem
                    key={index}
                    $selected={selectedIndices.includes(index)}
                    onClick={(e) => {
                      if (e.ctrlKey || e.metaKey) {
                        setSelectedCustomTags(prev => ({
                          ...prev,
                          [activeTab]: prev[activeTab]?.includes(index)
                            ? prev[activeTab].filter(i => i !== index)
                            : [...(prev[activeTab] || []), index]
                        }));
                      } else {
                        setSelectedCustomTags(prev => ({
                          ...prev,
                          [activeTab]: [index]
                        }));
                      }
                    }}
                    onDoubleClick={() => goToTag(tag)}
                  >
                    <div style={{ flex: 1, display: 'flex', flexDirection: 'column', gap: '4px' }}>
                      <div style={{ display: 'flex', gap: '10px', alignItems: 'center' }}>
                        <TagTimecode $selected={selectedIndices.includes(index)}>
                          {tag.timecode}
                        </TagTimecode>
                        <TagLabel $selected={selectedIndices.includes(index)}>
                          {tag.label}
                        </TagLabel>
                        <AddParameterButton
                          onClick={(e) => {
                            e.stopPropagation();
                            openTagEditor(activeTab, index);
                          }}
                          title="Éditer le tag (sequenceType et binding)"
                        >
                          ⚙️
                        </AddParameterButton>
                      </div>
                      {(tag.sequenceType || tag.keyBinding) && (
                        <TagParameters>
                          {tag.sequenceType && (
                            <ParameterBadge>
                              {tag.sequenceType === 'startSequence' ? 'Début séquence' : 'Fin séquence'}
                            </ParameterBadge>
                          )}
                          {tag.keyBinding && (
                            <ParameterBadge>
                              Touche: {tag.keyBinding}
                            </ParameterBadge>
                          )}
                        </TagParameters>
                      )}
                    </div>
                  </TagItem>
                );
              })
            )}
          </TagsList>
        </TagsPanel>
      </Content>

      <TimelineVisualization>
        {(() => {
          // Aligner tout sur la timeline principale (maxDuration)
          const currentLeft = maxDuration > 0 ? Math.max(0, Math.min(100, (currentPosition / maxDuration) * 100)) : 0;
          return (
            <>
              <CurrentPositionIndicator $left={currentLeft} />
              {sequences.map((seq, index) => {
                if (Array.isArray(seq.time_ms)) {
                  const [start, end] = seq.time_ms;
                  const left = maxDuration > 0 ? Math.max(0, Math.min(100, (start / maxDuration) * 100)) : 0;
                  const width = maxDuration > 0 ? Math.max(0, Math.min(100 - left, ((end - start) / maxDuration) * 100)) : 0;
                  return (
                    <SequenceBar
                      key={`seq-${index}`}
                      $left={left}
                      $width={width}
                      onClick={() => seek(start)}
                      title={seq.label}
                    />
                  );
                }
                return null;
              })}
              {labels.map((tag, index) => {
                if (!Array.isArray(tag.time_ms)) {
                  const left = maxDuration > 0 ? Math.max(0, Math.min(100, (tag.time_ms / maxDuration) * 100)) : 0;
                  const color = tag.sequenceType === 'startSequence' 
                    ? '#4CAF50' 
                    : tag.sequenceType === 'endSequence' 
                    ? '#F44336' 
                    : '#5FA8D3';
                  return (
                    <TagMarker
                      key={`label-${index}`}
                      $left={left}
                      $color={color}
                      onClick={() => seek(tag.time_ms as number)}
                      title={`${tag.label} - ${tag.timecode}`}
                    />
                  );
                }
                return null;
              })}
              {Object.entries(customTagTypes).map(([typeName, tags]) =>
                tags.map((tag, index) => {
                  if (!Array.isArray(tag.time_ms)) {
                    const left = maxDuration > 0 ? Math.max(0, Math.min(100, (tag.time_ms / maxDuration) * 100)) : 0;
                    const color = tag.sequenceType === 'startSequence' 
                      ? '#4CAF50' 
                      : tag.sequenceType === 'endSequence' 
                      ? '#F44336' 
                      : '#9C27B0';
                    return (
                      <TagMarker
                        key={`${typeName}-${index}`}
                        $left={left}
                        $color={color}
                        onClick={() => seek(tag.time_ms as number)}
                        title={`${tag.label} - ${tag.timecode}`}
                      />
                    );
                  }
                  return null;
                })
              )}
            </>
          );
        })()}
      </TimelineVisualization>

      <ControlsBottom>
        <PositionSlider
          type="range"
          min="0"
          max={maxDuration}
          value={currentPosition}
          onChange={(e) => seek(Number(e.target.value))}
        />
        <TimeLabel>
          {msToTimecode(currentPosition)} / {msToTimecode(maxDuration)}
        </TimeLabel>
      </ControlsBottom>

      {editingParameters && (() => {
        let tag: Tag | undefined;
        if (editingParameters.tab === 'sequences') {
          tag = sequences[editingParameters.index];
        } else if (editingParameters.tab === 'labels') {
          tag = labels[editingParameters.index];
        } else {
          tag = customTagTypes[editingParameters.tab]?.[editingParameters.index];
        }
        
        if (!tag) return null;
        
        return (
          <ModalOverlay onClick={closeParameterEditor}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Paramètres - {tag.label}</ModalTitle>
              
              <ParametersList>
                {tag.parameters && Object.entries(tag.parameters).map(([key, value]) => (
                  <ParameterItem key={key}>
                    <ParameterText>
                      <strong>{key}:</strong> {value}
                    </ParameterText>
                    <DeleteButton onClick={() => removeParameter(key)}>
                      Supprimer
                    </DeleteButton>
                  </ParameterItem>
                ))}
                {(!tag.parameters || Object.keys(tag.parameters).length === 0) && (
                  <ParameterText style={{ color: 'rgba(255, 255, 255, 0.5)', fontStyle: 'italic' }}>
                    Aucun paramètre
                  </ParameterText>
                )}
              </ParametersList>

              <div style={{ marginTop: '15px' }}>
                <ModalInput
                  type="text"
                  placeholder="Clé du paramètre"
                  value={parameterKey}
                  onChange={(e) => setParameterKey(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      const input = e.target as HTMLInputElement;
                      input.nextElementSibling?.querySelector('input')?.focus();
                    }
                  }}
                />
                <ModalInput
                  type="text"
                  placeholder="Valeur du paramètre"
                  value={parameterValue}
                  onChange={(e) => setParameterValue(e.target.value)}
                  onKeyDown={(e) => {
                    if (e.key === 'Enter') {
                      e.preventDefault();
                      addParameter();
                    }
                  }}
                />
                <div style={{ marginTop: '10px', display: 'flex', justifyContent: 'flex-end' }}>
                  <ModalButton onClick={addParameter}>
                    Ajouter
                  </ModalButton>
                  <ModalButton onClick={closeParameterEditor}>
                    Fermer
                  </ModalButton>
                </div>
              </div>
            </ModalContent>
          </ModalOverlay>
        );
      })()}

      {editingTag !== null && (() => {
        const { tab, index } = editingTag;
        let tag: Tag | undefined;
        
        if (tab === 'labels') {
          tag = labels[index];
        } else {
          tag = customTagTypes[tab]?.[index];
        }
        
        if (!tag) return null;
        
        return (
          <ModalOverlay onClick={closeTagEditor}>
            <ModalContent onClick={(e) => e.stopPropagation()}>
              <ModalTitle>Éditer le tag - {tag.label}</ModalTitle>
              
              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ffffff', fontSize: '14px' }}>
                  Type de séquence :
                </label>
                <ModalSelect
                  value={tagSequenceType || ''}
                  onChange={(e) => {
                    const value = e.target.value;
                    setTagSequenceType(value === '' ? null : value as 'startSequence' | 'endSequence');
                  }}
                >
                  <option value="">Aucun (tag normal)</option>
                  <option value="startSequence">Début de séquence</option>
                  <option value="endSequence">Fin de séquence</option>
                </ModalSelect>
              </div>

              <div style={{ marginBottom: '15px' }}>
                <label style={{ display: 'block', marginBottom: '8px', color: '#ffffff', fontSize: '14px' }}>
                  Touche clavier (binding) :
                </label>
                <KeyBindingInput
                  type="text"
                  placeholder={isCapturingKey ? "Appuyez sur une touche..." : "Cliquez pour capturer une touche"}
                  value={tagKeyBinding}
                  readOnly
                  onClick={() => {
                    setIsCapturingKey(true);
                    setTagKeyBinding('');
                  }}
                />
                {tagKeyBinding && (
                  <div style={{ marginTop: '8px', display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                    <span style={{ color: 'rgba(255, 255, 255, 0.7)', fontSize: '12px' }}>
                      Touche actuelle: <strong>{tagKeyBinding}</strong>
                    </span>
                    <DeleteButton
                      onClick={() => {
                        setTagKeyBinding('');
                        setIsCapturingKey(false);
                      }}
                    >
                      Supprimer
                    </DeleteButton>
                  </div>
                )}
                {isCapturingKey && (
                  <div style={{ marginTop: '8px', color: '#5FA8D3', fontSize: '12px', fontStyle: 'italic' }}>
                    Mode capture actif - Appuyez sur une touche pour l'associer
                  </div>
                )}
              </div>

              <div style={{ marginTop: '15px', display: 'flex', justifyContent: 'flex-end' }}>
                <ModalButton onClick={saveTagEdit}>
                  Enregistrer
                </ModalButton>
                <ModalButton onClick={closeTagEditor}>
                  Annuler
                </ModalButton>
              </div>
            </ModalContent>
          </ModalOverlay>
        );
      })()}
    </AppContainer>
  );
}

export default App;
